/**
 * Resolve once `isConnected()` returns true, polling every `intervalMs`. Resolves `false`
 * (without subscribing) when `isCancelled()` becomes true first — component destroyed or
 * tracker deselected while waiting.
 *
 * <p>Why: `initializeStomp()` resolves as soon as the STOMP client is *activated*, not once
 * the CONNECTED frame arrived. Subscribing in between throws inside @stomp/stompjs and the
 * subscription is silently lost forever. Same pattern as inside's `supervised.page.ts`
 * `whenWsReady`, extracted so it can be unit-tested without a component.
 */
export function waitForWebsocket(
  isConnected: () => boolean,
  isCancelled: () => boolean = () => false,
  intervalMs = 500
): Promise<boolean> {
  if (isCancelled()) return Promise.resolve(false);
  if (isConnected()) return Promise.resolve(true);
  return new Promise<boolean>(resolve => {
    const timer = setInterval(() => {
      if (isCancelled()) {
        clearInterval(timer);
        resolve(false);
        return;
      }
      if (isConnected()) {
        clearInterval(timer);
        resolve(true);
      }
    }, intervalMs);
  });
}
